import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { bloodTestsAPI } from '../services/api'
import MarkerInformation from '../components/MarkerInformation'
import MarkerValue from '../components/blood-tests/MarkerValue'
import InterpretabilityBadge from '../components/blood-tests/InterpretabilityBadge'
import MarkerContextSheet from '../components/blood-tests/MarkerContextSheet'
import ExcludedMarkersNotice from '../components/blood-tests/ExcludedMarkersNotice'
import StartHealthCycleButton from '../components/health-cycle/StartHealthCycleButton'
import AttachSupplementaryPrompt from '../components/health-cycle/AttachSupplementaryPrompt'
import toast, { Toaster } from 'react-hot-toast'
import {
  PlusIcon,
  BeakerIcon,
  DocumentArrowUpIcon,
  XMarkIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'
import { contentLanguage } from '../i18n/supported'

export default function BloodTests() {
  const { t, i18n } = useTranslation()
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [selectedTest, setSelectedTest] = useState(null)
  const [contextResult, setContextResult] = useState(null)
  const [uploadFile, setUploadFile] = useState(null)
  const [uploadData, setUploadData] = useState({
    test_date: new Date().toISOString().split('T')[0],
    lab_name: '',
    user_notes: ''
  })
  const [previousProcessingTests, setPreviousProcessingTests] = useState([])
  const [previousUploadedTests, setPreviousUploadedTests] = useState([])
  const [pendingUploadId, setPendingUploadId] = useState(null)
  // VH-233: the last upload — once its markers are extracted, offer it to the current cycle if partial
  const [suggestTestId, setSuggestTestId] = useState(null)

  // Translation state
  const [translatedContent, setTranslatedContent] = useState(null)
  const [isTranslating, setIsTranslating] = useState(false)
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language)

  // Listen for language changes to trigger re-render
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setCurrentLanguage(lng)
    }
    i18n.on('languageChanged', handleLanguageChange)
    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [i18n])

  const queryClient = useQueryClient()

  // Auto-refresh blood tests when there are processing tests
  // Polls every 1 second for fast status updates during parsing/analysis
  const { data: bloodTests, isLoading, isFetching: _isFetching } = useQuery({
    queryKey: ['bloodTests'],
    queryFn: () => bloodTestsAPI.getAll({ _t: Date.now() }),
    refetchInterval: (query) => {
      // React Query v5 passes the query object, not the data directly
      // Data is in query.state.data
      const data = query?.state?.data

      const hasProcessingTests = data?.data?.tests?.some(
        test => test.status === 'processing' || test.status === 'pending'
      )

      return hasProcessingTests ? 1000 : false // Poll every 1 second when processing
    },
    staleTime: 0, // Always consider data stale
    gcTime: 0, // Don't cache data (renamed from cacheTime in v5)
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData, // Keep showing old data while fetching (renamed from keepPreviousData)
  })


  // Watch for status changes and show toasts
  useEffect(() => {
    if (bloodTests?.data?.tests) {
      const currentUploadedTests = bloodTests.data.tests
        .filter(test => test.status === 'uploaded')
        .map(test => test.id)

      const currentProcessingTests = bloodTests.data.tests
        .filter(test => test.status === 'processing')
        .map(test => test.id)

      // Check if any uploaded tests started processing
      previousUploadedTests.forEach(testId => {
        if (!currentUploadedTests.includes(testId)) {
          const test = bloodTests.data.tests.find(t => t.id === testId)
          if (test && test.status === 'processing') {
            // Test started processing (uploaded → processing)
            // No toast needed here since we show "Analyzing markers..." on upload
          }
        }
      })

      // Check if any previously processing tests are now complete
      previousProcessingTests.forEach(testId => {
        if (!currentProcessingTests.includes(testId)) {
          const completedTest = bloodTests.data.tests.find(t => t.id === testId)
          if (completedTest) {
            // Invalidate detail cache to ensure it refetches with updated data
            queryClient.invalidateQueries({ queryKey: ['bloodTest', testId] })

            if (completedTest.status === 'uploaded') {
              // Dismiss loading toast and show success
              toast.success(t('bloodTests.upload.parsedSuccess'), {
                id: 'upload-toast',
                duration: 5000,
                position: 'top-right',
              })
            } else if (completedTest.status === 'analyzed') {
              // Dismiss loading toast and show success
              toast.success(t('bloodTests.upload.analyzedSuccess'), {
                id: 'upload-toast',
                duration: 5000,
                position: 'top-right',
              })
            }
          }
        }
      })

      setPreviousUploadedTests(currentUploadedTests)
      setPreviousProcessingTests(currentProcessingTests)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only reacts to bloodTests changes; other deps are stable refs
  }, [bloodTests])

  // Watch for pending upload to complete and dismiss the loading toast
  useEffect(() => {
    if (pendingUploadId && bloodTests?.data?.tests) {
      const uploadedTest = bloodTests.data.tests.find(t => t.id === pendingUploadId)
      console.log('[Upload] Checking pending upload:', pendingUploadId, 'found:', uploadedTest?.id, 'status:', uploadedTest?.status)
      if (uploadedTest && uploadedTest.status === 'analyzed') {
        console.log('[Upload] Analysis complete, showing success toast')
        // Analysis complete - dismiss loading toast and show success
        toast.success(t('bloodTests.upload.analyzedSuccess'), {
          id: 'upload-toast',
          duration: 5000,
          position: 'top-right',
        })
        setPendingUploadId(null)
      }
    }
  }, [bloodTests, pendingUploadId, t])

  const { data: testDetail } = useQuery({
    queryKey: ['bloodTest', selectedTest],
    queryFn: () => bloodTestsAPI.getById(selectedTest),
    enabled: !!selectedTest,
    refetchInterval: (data) => {
      // Poll every second if test is being processed
      const isProcessing = data?.data?.status === 'processing' || data?.data?.status === 'pending'
      return isProcessing ? 1000 : false
    },
    staleTime: 0
  })

  const { data: comparison } = useQuery({
    queryKey: ['bloodTestComparison', selectedTest],
    queryFn: () => bloodTestsAPI.compare(selectedTest),
    enabled: !!selectedTest
  })

  // Fetch translation when language changes or when viewing a new test
  useEffect(() => {
    const fetchTranslation = async () => {
      if (!testDetail?.data || !testDetail.data.ai_analysis_summary) {
        setTranslatedContent(null)
        return
      }

      const originalLang = testDetail.data.original_language || 'en'
      // Normalize language code (e.g., 'de-DE' -> 'de', 'en-US' -> 'en')
      const normalizedLang = contentLanguage(currentLanguage)

      // If viewing in original language, use original content
      if (normalizedLang === originalLang) {
        setTranslatedContent(null)
        return
      }

      // Fetch translation
      setIsTranslating(true)
      try {
        const response = await bloodTestsAPI.translate(testDetail.data.id, normalizedLang)
        setTranslatedContent(response.data)
      } catch (error) {
        console.error('Translation failed:', error)
        toast.error(t('bloodTests.translationFailed') || 'Failed to translate analysis')
        setTranslatedContent(null)
      } finally {
        setIsTranslating(false)
      }
    }

    fetchTranslation()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- testDetail.data is derived from query; using specific fields avoids unnecessary re-fetches
  }, [currentLanguage, testDetail?.data?.id, testDetail?.data?.ai_analysis_summary, t])

  // Helper to get displayed content (translated or original)
  const getDisplayedSummary = () => {
    if (translatedContent?.summary) return translatedContent.summary
    return testDetail?.data?.ai_analysis_summary
  }

  const getDisplayedRiskFactors = () => {
    if (translatedContent?.risk_factors) return translatedContent.risk_factors
    return testDetail?.data?.risk_factors
  }

  const _getDisplayedRecommendations = () => {
    if (translatedContent?.recommendations) return translatedContent.recommendations
    return testDetail?.data?.recommendations
  }

  const getDisplayedUserNotes = () => {
    if (translatedContent?.user_notes) return translatedContent.user_notes
    return testDetail?.data?.user_notes
  }

  const uploadMutation = useMutation({
    mutationFn: (formData) => bloodTestsAPI.upload(formData),
    onSuccess: (uploadResponse) => {
        // 1. IMMEDIATELY close modal and reset form (no awaits before this)
        setShowUploadModal(false)
        setUploadFile(null)
        setUploadData({
          test_date: new Date().toISOString().split('T')[0],
          lab_name: '',
          user_notes: ''
        })

        // 2. Track the uploaded test ID to dismiss toast when analysis completes
        setPendingUploadId(uploadResponse.data.blood_test_id)
        setSuggestTestId(uploadResponse.data.blood_test_id)

        // 3. Show persistent loading toast (will be dismissed when analysis completes)
        toast.loading(t('bloodTests.upload.uploadComplete'), {
          id: 'upload-toast',
          position: 'top-right',
        })

        // 4. Invalidate cache - polling will automatically refetch and show new test
        // No await needed - let it run in background
        queryClient.invalidateQueries({ queryKey: ['bloodTests'] })
      },
      onError: (_error) => {
        toast.error(t('bloodTests.upload.uploadFailed'), {
          id: 'upload-toast',
          duration: 4000,
          position: 'top-right',
        })
      }
    })

  const analyzeMutation = useMutation({
    mutationFn: (id) => bloodTestsAPI.analyze(id),
    onSuccess: async (response) => {
      // Backend returns updated blood test with 'processing' status
      // Immediately update detail view cache
      // IMPORTANT: Keep the axios response structure { data: {...} } to match natural fetches
      queryClient.setQueryData(['bloodTest', selectedTest], response)

      // Update list cache to show processing status immediately
      // Preserve all test data but update status from backend response
      queryClient.setQueryData(['bloodTests'], (oldData) => {
        if (!oldData?.data?.tests) return oldData

        return {
          ...oldData,
          data: {
            ...oldData.data,
            tests: oldData.data.tests.map(test =>
              test.id === selectedTest ? { ...test, ...response.data } : test
            )
          }
        }
      })

      // Invalidate and refetch to trigger polling
      await queryClient.invalidateQueries({ queryKey: ['bloodTests'] })
      await queryClient.refetchQueries({ queryKey: ['bloodTests'] })
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => bloodTestsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bloodTests'] })
      setSelectedTest(null)
    }
  })

  const handleUpload = async (e) => {
    e.preventDefault()

    if (!uploadFile) {
      toast.error('Please select a PDF file', {
        position: 'top-right',
      })
      return
    }

    toast.loading('Uploading blood test...', {
      id: 'upload-toast',
      position: 'top-right',
    })

    const formData = new FormData()
    formData.append('file', uploadFile)
    if (uploadData.test_date) formData.append('test_date', uploadData.test_date)
    if (uploadData.lab_name) formData.append('lab_name', uploadData.lab_name)
    if (uploadData.user_notes) formData.append('user_notes', uploadData.user_notes)

    uploadMutation.mutate(formData)
  }

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      uploaded: 'bg-gray-100 text-gray-800',
      processing: 'bg-blue-100 text-blue-800 animate-pulse',
      analyzed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    }
    return badges[status] || badges.uploaded
  }

  const getStatusIcon = (status) => {
    if (status === 'pending') {
      return <ArrowPathIcon className="h-4 w-4 animate-spin inline mr-1" />
    } else if (status === 'processing') {
      return <ArrowPathIcon className="h-4 w-4 animate-spin inline mr-1" />
    } else if (status === 'analyzed') {
      return <CheckCircleIcon className="h-4 w-4 inline mr-1" />
    } else if (status === 'failed') {
      return <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
    }
    return null
  }

  const getStatusText = (status) => {
    return t(`bloodTests.status.${status}`, status)
  }

  const getHealthScoreBadge = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-800'
    if (score >= 60) return 'bg-yellow-100 text-yellow-800'
    return 'bg-red-100 text-red-800'
  }

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center h-64">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Toaster position="top-right" />
      {/* VH-221: why a marker cannot be judged */}
      <MarkerContextSheet
        bloodTestId={selectedTest}
        resultId={contextResult?.id}
        markerName={contextResult?.marker_name}
        open={Boolean(contextResult)}
        onClose={() => setContextResult(null)}
      />
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('bloodTests.title')}</h1>
          <p className="mt-2 text-gray-600">
            {t('bloodTests.subtitle')}
          </p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/blood-tests/narrative-report"
            className="inline-flex items-center px-4 py-2 border border-green-600 rounded-md shadow-sm text-sm font-medium text-green-600 bg-surface-container hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <DocumentTextIcon className="h-5 w-5 mr-2" />
            {t('reports.narrative.title', 'Narrative Report')}
          </Link>
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-on-primary bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('bloodTests.uploadTest')}
          </button>
        </div>
      </div>

      {suggestTestId && suggestTestId !== selectedTest && (
        <div className="mb-6">
          <AttachSupplementaryPrompt
            bloodTestId={suggestTestId}
            enabled={['uploaded', 'analyzed'].includes(bloodTests?.data?.tests?.find((x) => x.id === suggestTestId)?.status)}
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Blood Tests List */}
        <div className="lg:col-span-1">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('bloodTests.yourTests')}</h2>
          {bloodTests?.data?.tests?.length > 0 ? (
            <div className="space-y-3">
              {bloodTests.data.tests.map((test) => (
                <div
                  key={test.id}
                  onClick={() => setSelectedTest(test.id)}
                  className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${
                    selectedTest === test.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <BeakerIcon className="h-6 w-6 text-primary-600 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">
                          {test.lab_name || 'Blood Test'}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {new Date(test.test_date).toLocaleDateString()}
                        </p>
                        <span className={`inline-flex mt-1 items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(test.status)}`}>
                          {getStatusIcon(test.status)}
                          {getStatusText(test.status)}
                        </span>
                      </div>
                    </div>
                    {test.health_score && (
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${getHealthScoreBadge(test.health_score)}`}>
                        {test.health_score}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <BeakerIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('bloodTests.noTests')}</h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('bloodTests.noTestsDescription')}
              </p>
            </div>
          )}
        </div>

        {/* Blood Test Detail */}
        <div className="lg:col-span-2">
          {selectedTest && testDetail?.data ? (
            <div className="space-y-6">
              {['uploaded', 'analyzed'].includes(testDetail.data.status) && (
                <AttachSupplementaryPrompt bloodTestId={testDetail.data.id} />
              )}
              {/* Header */}
              <div className="bg-surface-container rounded-lg shadow p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {testDetail.data.lab_name || 'Blood Test Results'}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {t('bloodTests.testDate')}: {new Date(testDetail.data.test_date).toLocaleDateString()}
                    </p>
                    {testDetail.data.user_notes && (
                      <p className="text-sm text-gray-600 mt-2 italic">
                        {t('common.notes')}: {getDisplayedUserNotes()}
                      </p>
                    )}
                    <span className={`inline-flex mt-2 items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(testDetail.data.status)}`}>
                      {getStatusIcon(testDetail.data.status)}
                      {getStatusText(testDetail.data.status)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-start gap-2">
                    {/* VH-227: start the health cycle from an analysed test; opens it on /health-cycle */}
                    {testDetail.data.status === 'analyzed' && (
                      <StartHealthCycleButton bloodTestId={testDetail.data.id} />
                    )}
                    {(testDetail.data.status === 'uploaded' || testDetail.data.status === 'analyzed') && (
                      <button
                        onClick={() => analyzeMutation.mutate(selectedTest)}
                        disabled={analyzeMutation.isLoading || testDetail.data.status === 'processing'}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-on-neutral bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        <ChartBarIcon className="h-4 w-4 mr-2" />
                        {analyzeMutation.isLoading ? 'Analyzing...' : testDetail.data.status === 'analyzed' ? 'Re-analyze' : 'Analyze with AI'}
                      </button>
                    )}
                    <button
                      onClick={() => deleteMutation.mutate(selectedTest)}
                      className="text-red-600 hover:text-red-800 text-sm px-4 py-2"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Info message for uploaded but not analyzed tests */}
                {testDetail.data.status === 'uploaded' && (
                  <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <ChartBarIcon className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          <strong>Step 1 Complete:</strong> Blood markers have been extracted and stored.
                          Click &quot;Analyze with AI&quot; to get health insights, risk assessment, and personalized recommendations.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {testDetail.data.health_score && (
                  <div className="mt-6">
                    {/* Health Score */}
                    <div className="text-center mb-6">
                      <p className="text-sm text-gray-500">{t('bloodTests.healthScore')}</p>
                      <p className={`text-3xl font-bold mt-1 ${
                        testDetail.data.health_score >= 80 ? 'text-green-600' :
                        testDetail.data.health_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {testDetail.data.health_score}
                      </p>
                      <div className="flex justify-center">
                        <ExcludedMarkersNotice
                          bloodTestId={testDetail.data.id}
                          excludedCount={testDetail.data.score_excluded_count || 0}
                          contextCheckStatus={testDetail.data.context_check_status}
                        />
                      </div>
                    </div>

                    {/* Marker Counts - Two Section Layout */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* AI Analysis */}
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                          <span className="mr-1">🤖</span>
                          {t('bloodTests.aiRiskFactors')}
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-700">{t('bloodTests.totalRiskFactors')}:</span>
                            <span className="text-lg font-bold text-blue-600">
                              {testDetail.data.ai_risk_factors_count || 0}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-700">{t('bloodTests.criticalSeverity')}:</span>
                            <span className="text-lg font-bold text-red-600">
                              {testDetail.data.ai_critical_count || 0}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-700">{t('bloodTests.highSeverity')}:</span>
                            <span className="text-lg font-bold text-orange-600">
                              {testDetail.data.ai_high_count || 0}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Parsed Markers */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                          <span className="mr-1">📊</span>
                          {t('bloodTests.parsedMarkers')}
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-700">{t('bloodTests.totalMarkers')}:</span>
                            <span className="text-lg font-bold text-gray-700">
                              {testDetail.data.test_results?.length || 0}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-700">{t('bloodTests.abnormalStatus')}:</span>
                            <span className="text-lg font-bold text-yellow-600">
                              {testDetail.data.parsed_abnormal_count || 0}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-1">
                              <span className="text-sm text-gray-700">Critical Flags:</span>
                              <div className="relative group">
                                <InformationCircleIcon className="h-4 w-4 text-gray-400 cursor-help" />
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10 w-80">
                                  <div className="bg-gray-900 text-on-neutral text-xs rounded-lg p-3 shadow-lg">
                                    <p className="font-semibold mb-2">Critical Value Detection:</p>
                                    <p className="mb-2">Markers are flagged as critical when:</p>
                                    <ul className="list-disc list-inside space-y-1 mb-2">
                                      <li>Values exceed marker-specific dangerous thresholds (e.g., Hemoglobin &lt;70 or &gt;200 g/L)</li>
                                      <li>Values deviate more than 1 full reference range from normal limits</li>
                                    </ul>
                                    <p className="text-yellow-200 font-semibold">⚠️ Critical values require immediate medical consultation</p>
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                      <div className="border-8 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <span className="text-lg font-bold text-red-600">
                              {testDetail.data.parsed_critical_count || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Translation in progress banner */}
              {isTranslating && (
                <div className="bg-indigo-100 border border-indigo-300 rounded-lg p-4 flex items-center justify-center">
                  <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin text-indigo-600" />
                  <span className="text-indigo-700 font-medium">{t('bloodTests.translatingAnalysis')}</span>
                </div>
              )}

              {/* Overall Health Summary */}
              {testDetail.data.ai_analysis_summary && (
                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <ChartBarIcon className="h-5 w-5 mr-2 text-blue-600" />
                    {t('bloodTests.overallHealthSummary')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{getDisplayedSummary()}</p>
                </div>
              )}

              {/* Risk Factors */}
              {getDisplayedRiskFactors() && getDisplayedRiskFactors().length > 0 && (
                <div className="bg-yellow-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 mr-2 text-yellow-600" />
                    {t('bloodTests.riskFactors')}
                  </h3>
                  <div className="space-y-4">
                    {getDisplayedRiskFactors().map((risk, idx) => {
                      const description = typeof risk === 'string' ? risk : risk.description
                      const marker = typeof risk === 'object' ? risk.marker : null
                      const severity = typeof risk === 'object' ? risk.severity : null

                      return (
                        <div key={idx} className="border-l-4 border-yellow-400 pl-4 py-2">
                          {marker && (
                            <div className="font-semibold text-gray-900 mb-1 flex items-center">
                              {marker}
                              {severity && (
                                <span className={`ml-2 px-2 py-0.5 text-xs rounded ${
                                  severity === 'critical' ? 'bg-red-100 text-red-800' :
                                  severity === 'high' ? 'bg-orange-100 text-orange-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {severity}
                                </span>
                              )}
                            </div>
                          )}
                          <div className="text-gray-700 text-sm whitespace-pre-line">
                            {description}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Recommendations section removed - comprehensive summary now shown in AI Analysis Summary above */}

              {/* Test Results */}
              {testDetail.data.test_results && testDetail.data.test_results.length > 0 && (
                <div className="bg-surface-container rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('bloodTests.testResults')}</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('bloodTests.marker')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('bloodTests.value')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('bloodTests.reference')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-surface-container divide-y divide-gray-200">
                        {testDetail.data.test_results.map((result, idx) => (
                          <tr key={idx} className={result.is_abnormal ? 'bg-yellow-50' : ''}>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              <div className="flex flex-col">
                                <span>{result.marker_name}</span>
                                <InterpretabilityBadge
                                  interpretability={result.interpretability}
                                  onClick={() => setContextResult(result)}
                                />
                                <MarkerInformation
                                  markerName={result.marker_name}
                                  value={result.value}
                                  status={result.status}
                                />
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              <MarkerValue result={result} />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {result.reference_range}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded ${
                                result.status === 'normal' ? 'bg-green-100 text-green-800' :
                                result.status === 'borderline' ? 'bg-yellow-100 text-yellow-800' :
                                result.status === 'abnormal' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {t(`bloodTests.markerStatus.${result.status}`, result.status)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Comparison with Previous Test */}
              {comparison?.data?.previous_test && (
                <div className="bg-surface-container rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('bloodTests.trendAnalysis')}</h3>

                  {comparison.data.trends?.improving?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-green-600 mb-2">{t('bloodTests.improving')} ↑</h4>
                      <ul className="space-y-1">
                        {comparison.data.trends.improving.map((trend, idx) => (
                          <li key={idx} className="text-sm text-gray-700">
                            • {trend.marker}: {trend.previous_status} → {trend.current_status}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {comparison.data.trends?.worsening?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-red-600 mb-2">{t('bloodTests.worsening')} ↓</h4>
                      <ul className="space-y-1">
                        {comparison.data.trends.worsening.map((trend, idx) => (
                          <li key={idx} className="text-sm text-gray-700">
                            • {trend.marker}: {trend.previous_status} → {trend.current_status}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {comparison.data.trends?.overall_trend && (
                    <p className="text-sm text-gray-600 mt-2">
                      {t('bloodTests.overallTrend')}: <span className="font-semibold">{comparison.data.trends.overall_trend}</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-12 text-center">
              <BeakerIcon className="mx-auto h-16 w-16 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">{t('bloodTests.selectTestPrompt')}</h3>
              <p className="mt-2 text-sm text-gray-500">
                {t('bloodTests.selectTestDescription')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">{t('bloodTests.upload.title')}</h2>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('bloodTests.upload.pdfFile')} *
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-primary-400 transition-colors">
                  <div className="space-y-1 text-center">
                    <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label className="relative cursor-pointer bg-surface-container rounded-md font-medium text-primary-600 hover:text-primary-500">
                        <span>{t('bloodTests.upload.uploadFile')}</span>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setUploadFile(e.target.files[0])}
                          className="sr-only"
                          required
                        />
                      </label>
                      <p className="pl-1">{t('bloodTests.upload.dragDrop')}</p>
                    </div>
                    <p className="text-xs text-gray-500">{t('bloodTests.upload.pdfLimit')}</p>
                    {uploadFile && (
                      <p className="text-sm text-green-600 mt-2">
                        {t('bloodTests.upload.selected')}: {uploadFile.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('bloodTests.testDate')}
                </label>
                <input
                  type="date"
                  value={uploadData.test_date}
                  onChange={(e) => setUploadData({ ...uploadData, test_date: e.target.value })}
                  className="mt-1 block w-full py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('bloodTests.labName')}
                </label>
                <input
                  type="text"
                  value={uploadData.lab_name}
                  onChange={(e) => setUploadData({ ...uploadData, lab_name: e.target.value })}
                  className="mt-1 block w-full py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('common.notes')}
                </label>
                <textarea
                  value={uploadData.user_notes}
                  onChange={(e) => setUploadData({ ...uploadData, user_notes: e.target.value })}
                  rows={3}
                  className="mt-1 block w-full py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 bg-surface-container py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={uploadMutation.isLoading}
                  className="flex-1 bg-primary-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {uploadMutation.isLoading ? t('bloodTests.upload.uploading') : t('bloodTests.upload.uploadAndAnalyze')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
